import { openDialog } from '../../../store/actions/page.action';
import { Store } from '@ngrx/store';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Host,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  SkipSelf,
  ViewChild,
} from '@angular/core';
import { ISelect, ISelectItem } from 'app/store/models/select.model';
import { IReducers } from 'app/store/reducers';
import { ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { AbstractControl, ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent
  implements OnInit, ControlValueAccessor, OnChanges {
  @ViewChild('selectElement') selectElement: ElementRef<HTMLSelectElement>;

  @Input() defaultValue: any;

  @Input() items: ISelectItem[] | Observable<ISelectItem[]>;

  @Input() label: string;

  @Input() properties: ISelect<any>;

  @Input() confirmMessage: string;

  @Input() multi: boolean;

  @Input() dropdownPosition: 'bottom' | 'top' | 'auto' = 'auto';

  @Input() resetSelection: boolean;

  @Input() colorWhite: boolean;

  @Input() trackBy: string;

  @Input() id: string;

  @Output() selection: EventEmitter<any> = new EventEmitter<any>();

  value: any;
  @Input() disabled: boolean;
  selectedLabel: string;
  ready: boolean;

  control: AbstractControl;

  autoID: string;

  @Input() formControlName: string;

  private defaultValuePlaceholder = 'Select...';

  /**
   * Internal value that holds both label and the value
   * @private
   */
  private internalValue: any;

  private innerItems: ISelectItem[] = [];

  constructor(
    private store: Store<IReducers>,
    @Optional()
    @Host()
    @SkipSelf()
    private controlContainer: ControlContainer,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.autoID = (new Date('now')).getTime().toString();
  }

  propagateChange = (_: any) => {
  };

  propagateTouch = (_: any) => {
  };

  writeValue(obj: any, emitChanges: boolean = true): void {
    if (typeof obj === 'string') {
      // case when the writeValue is passed with string. This was used before
      obj = {
        value: obj,
      };
    }
    if ((!obj || obj.value === undefined) && obj !== 0 && !this.multi) {
      return;
    }
    this.internalValue = obj;
    if (this.confirmMessage) {
      this.store.dispatch(
        openDialog({
          dialogData: {
            title: 'Please confirm',
            canClose: false,
            content: this.confirmMessage,
            dialogMinWidth: '500px',
            onCloseButton: dialog => {
              this._write(this.defaultValue, emitChanges);
              dialog.closeDialog();
            },
            buttons: [
              {
                actAsClose: true,
                color: ButtonColor.GREEN,
                size: ButtonSize.SMALL,
                text: 'Ok',
                onClick: dialog => {
                  this._write(obj.value, emitChanges);
                  dialog.closeDialog();
                },
              },
            ],
          },
        }),
      );
    } else {
      this._write(!this.multi ? obj.value : obj, emitChanges);
    }

    if (this.resetSelection) {
      // use setTimeout to make sure the value is set before the dropdown is opened
      setTimeout(() => {
        this.resetSelected();
      });
    }
  }

  resetSelected() {
    this.value = this.innerItems[0].value;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit() {
    if (this.items instanceof Observable) {
      this.items.subscribe(items => {
        this.innerItems = items;
        this.init();
      });

      return;
    }

    this.innerItems = this.items as ISelectItem[];
    this.init();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.items instanceof Observable && this.trackBy) {
      if (
        changes.trackBy &&
        changes.trackBy.currentValue &&
        changes.trackBy.previousValue &&
        changes.trackBy.currentValue !== changes.trackBy.previousValue
      ) {
        if (!this.properties) {
          this.properties = { items: [], label: this.label };
        }
        this.properties.items = [];
        this.items.subscribe(items => {
          this.innerItems = items;
          for (const item of this.innerItems) {
            if (typeof item === 'object') {
              if (
                !this.properties.items
                  .map(i => JSON.stringify(i))
                  .includes(JSON.stringify(item))
              ) {
                this.properties.items.push(item);
              }
            } else if (!this.properties.items.includes(item)) {
              this.properties.items.push(item);
            }
          }
        });
      }

      return;
    }
    if (
      changes.items &&
      JSON.stringify(changes.items.currentValue) !==
      JSON.stringify(changes.items.previousValue)
    ) {
      if (!this.properties || !this.properties.items) {
        this.properties = { items: [], label: this.label };
      }

      this.properties.items = [];

      for (const item of changes.items.currentValue) {
        if (typeof item === 'object') {
          if (
            !this.properties.items
              .map(i => JSON.stringify(i))
              .includes(JSON.stringify(item))
          ) {
            this.properties.items.push(item);
          }
        } else if (!this.properties.items.includes(item)) {
          this.properties.items.push(item);
        }
      }

      this.innerItems = changes.items.currentValue;
    }
    if (
      changes.innerItems &&
      JSON.stringify(changes.innerItems.currentValue) !==
      JSON.stringify(changes.innerItems.previousValue)
    ) {
      if (!this.properties || !this.properties.items) {
        this.properties = { items: [], label: this.label };
      }

      this.properties.items = [];

      for (const item of this.innerItems) {
        if (typeof item === 'object') {
          if (
            !this.properties.items
              .map(i => JSON.stringify(i))
              .includes(JSON.stringify(item))
          ) {
            this.properties.items.push(item);
          }
        } else if (!this.properties.items.includes(item)) {
          this.properties.items.push(item);
        }
      }
    }
  }

  findLabel(value: any) {
    let targetItem = null;
    let notFound = 0;
    for (const item of this.properties.items) {
      if (typeof item.value === 'object' && typeof value === 'object') {
        if (JSON.stringify(item.value) === JSON.stringify(value)) {
          targetItem = item;
          break;
        } else {
          notFound++;
        }
      } else if (
        item &&
        typeof item.value !== 'object' &&
        value &&
        value.value !== undefined
      ) {
        if (item.value === value.value) {
          targetItem = item;
          break;
        } else {
          notFound++;
        }
      } else if (typeof item.value === typeof value) {
        if (item.value === value) {
          // check if there are more than one item with the same value
          const existingValues = this.properties.items.filter(
            i => i.value === value,
          );
          if (existingValues.length > 1 && this.internalValue) {
            // more than one item was found, so find the correct item by it's label
            const correctItem = this.properties.items.find(
              i =>
                i.label === this.internalValue.label &&
                i.value === this.internalValue.value,
            );

            if (correctItem) {
              targetItem = correctItem;
              break;
            } else {
              notFound++;
            }
          }
          targetItem = item;
          break;
        } else {
          notFound++;
        }
      } else {
        notFound++;
      }
    }
    if (notFound === this.properties.items.length) {
      return this.defaultValue?.value;
    }
    if (!targetItem) {
      return '';
    }
    return targetItem.label;
  }

  onClear($event: any) {
    this._write(this.defaultValue.value || this.defaultValue);
  }

  private _write(value, emitChanges: boolean = true) {
    this.value = value;

    this.propagateChange(this.value);

    if (this.value || this.value === 0) {
      this.propagateTouch(this.value);
    }

    if (emitChanges) {
      if (this.multi && Array.isArray(this.value)) {
        this.selection.next(this.value.map(v => v.value));
        return;
      }
      this.selection.next(
        this.value === this.defaultValuePlaceholder ? null : this.value,
      );
    }
  }

  private confirmSelection(value) {
    this.propagateChange(value);
    this.selection.emit(value);
  }

  private init() {
    if (this.controlContainer) {
      if (this.formControlName) {
        this.control = this.controlContainer.control.get(this.formControlName);
      }
    }

    if (this.control) {
      this.defaultValue = this.innerItems.find(
        i => i.value === this.control.value,
      );
      this._write(this.control.value, true);
    } else {
      if (this.defaultValue === undefined) {
        this.innerItems.filter(i => i.selected).map(i => this.defaultValue);
      }

      if (
        (this.defaultValue === undefined ||
          this.defaultValue === null ||
          this.defaultValue === 0) &&
        !this.multi
      ) {
        this.defaultValue = {
          label: this.defaultValuePlaceholder,
          value: this.defaultValuePlaceholder,
        };
        this.innerItems = [this.defaultValue, ...this.innerItems];
        this._write(this.defaultValue.value, false);
      } else if (
        this.defaultValue !== undefined &&
        this.defaultValue !== null &&
        typeof this.defaultValue !== 'object' &&
        !this.multi
      ) {
        const targetValue = this.innerItems.find(
          i => i.value === this.defaultValue,
        );
        if (!targetValue) {
          const oldDefaultValue = 0 + this.defaultValue;
          this.defaultValue = {
            value: '',
            label: 'Select...',
          };
          this._write(oldDefaultValue, false);
        } else {
          this.defaultValue = targetValue;
          this._write(targetValue, false);
        }
      }
    }

    if (!this.properties) {
      this.properties = {
        items: this.innerItems,
        label: this.label,
      };
    } else {
      if (!this.properties.items) {
        this.properties.items = this.innerItems;
      }
      if (!this.properties.label) {
        this.properties.label = this.label;
      }
    }
    this.ready = true;
  }
}
